/* Formato condicional y listas desplegables de FiboSheets.
 *
 * ── PARA QUÉ ────────────────────────────────────────────────────────────────
 *
 * «El CRT en rojo si vence en menos de 15 días.» «El estado sólo puede ser En
 * ruta, En base o Taller.» Son las dos cosas que la gente arma en su Excel para
 * que la planilla le AVISE en vez de tener que leerla entera. Acá viven puras
 * —entra una hoja, salen colores y listas— y se prueban sin dibujar nada. La
 * pantalla (`routes/MiEspacio.jsx` y `routes/PanelCondicional.jsx`) sólo las
 * llama.
 *
 * ── CÓMO SE GUARDA ──────────────────────────────────────────────────────────
 *
 * Dentro de cada hoja del libro (ver `empaquetarLibro` en MiEspacio):
 *
 *   hoja.condicional = [{ rango: "E2:E100", tipo: "venceEn", dias: 15,
 *                         estilo: { bg: "#ffcdd2", color: "#c0392b", bold: true } }, …]
 *   hoja.validacion  = [{ rango: "C2:C50", lista: ["En ruta", "En base", "Taller"] },
 *                       { rango: "D2:D50", fuente: "=Listas!A1:A5" }, …]
 *
 * El rango va escrito como en Excel (sin hoja: es siempre de la hoja que lo
 * tiene), así se lee en la base y se corre con el mismo `ajustarFormula` que
 * las fórmulas cuando se insertan o borran filas y columnas.
 *
 * ── EL ORDEN DE LAS REGLAS ──────────────────────────────────────────────────
 *
 * Se miran en el orden de la lista y LA PRIMERA QUE SE CUMPLE pinta la celda
 * (las de abajo no suman nada a esa celda). Así «vencida → rojo» arriba de
 * «vence en 15 días → amarillo» hace lo que uno espera sin que los colores se
 * mezclen. Las listas desplegables, igual: si dos tocan la misma celda, manda
 * la primera.
 *
 * ── SOBRE EL VALOR CALCULADO ────────────────────────────────────────────────
 *
 * Las condiciones miran lo que la celda MUESTRA (el resultado de su fórmula),
 * no su texto crudo. `calcularFormato` recibe los valores que la pantalla ya
 * calculó y no recalcula la hoja: las condiciones comunes miran esa matriz, y
 * las «fórmulas personalizadas» se calculan TODAS JUNTAS en una sola pasada
 * del motor, sobre una hoja de mentira que sólo tiene esas fórmulas (ver
 * `calcularFormulas`): el motor calcula lo que ellas miran y nada más. Sale un
 * Map por referencia, una vez por render.
 *
 * El motor (`shared/planilla.js`) no se toca a propósito: tiene una copia
 * idéntica en la edge function `evaluar-planilla` (ver evaluarPlanilla.test).
 */

import { ajustarFormula, desplazarFormula, evaluar, fechaASerial, numeroLiteral, parseRef, refDe } from "./planilla";

/* ── Los tipos de regla ─────────────────────────────────────────────────────
 * `campos` es lo que la pantalla tiene que pedir para cada uno. */
export const TIPOS = [
  { tipo: "mayor", etiqueta: "Mayor que", campos: ["valor"] },
  { tipo: "menor", etiqueta: "Menor que", campos: ["valor"] },
  { tipo: "entre", etiqueta: "Entre", campos: ["valor", "valor2"] },
  { tipo: "igual", etiqueta: "Igual a (número)", campos: ["valor"] },
  { tipo: "contiene", etiqueta: "El texto contiene", campos: ["texto"] },
  { tipo: "textoIgual", etiqueta: "El texto es igual a", campos: ["texto"] },
  { tipo: "vencida", etiqueta: "Fecha vencida (antes de hoy)", campos: [] },
  { tipo: "venceEn", etiqueta: "Vence en los próximos N días", campos: ["dias"] },
  { tipo: "vacia", etiqueta: "Está vacía", campos: [] },
  { tipo: "noVacia", etiqueta: "No está vacía", campos: [] },
  { tipo: "formula", etiqueta: "Fórmula personalizada", campos: ["formula"] },
];
const POR_TIPO = new Map(TIPOS.map((t) => [t.tipo, t]));

/* La paleta de las reglas: la de la barra de formato más un rojo y un amarillo
   de alerta, que es para lo que más se usa. "" es «sin color». */
export const FONDOS_REGLA = ["", "#ffcdd2", "#fff3b0", "#c8e6c9", "#bbdefb", "#ffe0b2", "#e0e0e0"];
export const TEXTOS_REGLA = ["", "#c0392b", "#b26a00", "#1e7e34", "#1565c0", "#555555"];
export const ESTILO_INICIAL = { bg: "#ffcdd2", color: "#c0392b" };

const MAX_OPCIONES = 500; // una lista desplegable más larga no se puede elegir con el dedo
const MAX_CELDAS_FUENTE = 2000;

/* ── Rangos ─────────────────────────────────────────────────────────────── */

/* "E2:E100" (o "E2", o con $) → { r0, c0, r1, c1 }, o null. */
export function parseRango(txt) {
  const s = String(txt ?? "").trim().replace(/^=/, "");
  const [a, b, ...resto] = s.split(":");
  if (resto.length) return null;
  const p = parseRef(a);
  const q = b === undefined ? p : parseRef(b);
  if (!p || !q) return null;
  return {
    r0: Math.min(p.fila, q.fila), r1: Math.max(p.fila, q.fila),
    c0: Math.min(p.col, q.col), c1: Math.max(p.col, q.col),
  };
}

export function textoRango(rg) {
  const a = refDe(rg.r0, rg.c0);
  return rg.r0 === rg.r1 && rg.c0 === rg.c1 ? a : `${a}:${refDe(rg.r1, rg.c1)}`;
}

const seCruzan = (a, b) => a.r0 <= b.r1 && b.r0 <= a.r1 && a.c0 <= b.c1 && b.c0 <= a.c1;
const recortar = (a, b) => ({
  r0: Math.max(a.r0, b.r0), r1: Math.min(a.r1, b.r1), c0: Math.max(a.c0, b.c0), c1: Math.min(a.c1, b.c1),
});

/* Los índices de las reglas (de formato o de lista) que tocan el rango. */
export function reglasEn(reglas, rg) {
  const out = [];
  (reglas || []).forEach((x, i) => {
    const r = parseRango(x && x.rango);
    if (r && rg && seCruzan(r, rg)) out.push(i);
  });
  return out;
}

/* ── Lo guardado → lo que se usa ────────────────────────────────────────────
 * Lo que viene de la base puede venir roto (una versión vieja, una edición a
 * mano): lo que no se entiende se descarta en vez de romper la planilla. */

const ESTILO_OK = ["bg", "color", "bold"];
function limpiarEstilo(e) {
  const out = {};
  for (const k of ESTILO_OK) if (e && e[k]) out[k] = k === "bold" ? true : String(e[k]);
  return out;
}

export function normalizarReglas(lista) {
  if (!Array.isArray(lista)) return [];
  return lista.filter((x) => x && POR_TIPO.has(x.tipo) && parseRango(x.rango))
    .map((x) => ({ ...x, estilo: limpiarEstilo(x.estilo) }));
}

export function normalizarValidaciones(lista) {
  if (!Array.isArray(lista)) return [];
  return lista.filter((x) => x && parseRango(x.rango)
    && ((Array.isArray(x.lista) && x.lista.length) || (typeof x.fuente === "string" && x.fuente.trim())));
}

/* ── Cuándo se cumple una regla ─────────────────────────────────────────── */

/* «Hoy» como serial de fecha, en hora ARGENTINA (UTC-3 fijo, como el motor y
   `shared/fechaAr`): a las 22 h no puede pasar a ser mañana. */
export function hoySerial(ahora = new Date()) {
  const d = new Date(ahora.getTime() - 3 * 3600 * 1000);
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  return fechaASerial(`${dd}/${mm}/${d.getUTCFullYear()}`);
}

/* Lo que se ve de un valor, para comparar textos. Los números con coma, como
   se escriben acá. */
export function textoDe(v) {
  if (v === "" || v == null) return "";
  if (typeof v === "boolean") return v ? "VERDADERO" : "FALSO";
  if (typeof v === "number") return String(v).replace(".", ",");
  return String(v);
}

const esError = (v) => typeof v === "string" && v.startsWith("#");

/* Una fecha DD/MM/AAAA (con o sin hora atrás) → serial, o null. Un número NO
   se toma como fecha: 45000 puede ser cualquier cosa. */
function fechaDe(v) {
  if (typeof v !== "string") return null;
  return fechaASerial(v.trim().split(/\s+/)[0]);
}

/* ¿La regla se cumple para este valor? (las de fórmula se miran aparte). */
export function cumple(regla, v, hoy = hoySerial()) {
  if (!regla) return false;
  switch (regla.tipo) {
    case "mayor": return typeof v === "number" && typeof regla.valor === "number" && v > regla.valor;
    case "menor": return typeof v === "number" && typeof regla.valor === "number" && v < regla.valor;
    case "igual": return typeof v === "number" && typeof regla.valor === "number" && Math.abs(v - regla.valor) < 1e-9;
    case "entre": {
      if (typeof v !== "number" || typeof regla.valor !== "number" || typeof regla.valor2 !== "number") return false;
      return v >= Math.min(regla.valor, regla.valor2) && v <= Math.max(regla.valor, regla.valor2);
    }
    case "contiene": {
      const q = String(regla.texto ?? "").trim().toLowerCase();
      return !!q && !esError(v) && textoDe(v).toLowerCase().includes(q);
    }
    case "textoIgual":
      return !esError(v) && textoDe(v).trim().toLowerCase() === String(regla.texto ?? "").trim().toLowerCase();
    case "vencida": {
      const f = fechaDe(v);
      return f !== null && f < hoy;
    }
    case "venceEn": {
      // Desde hoy hasta dentro de N días, incluidos. Lo ya vencido NO entra: va
      // con su propia regla («Fecha vencida»), que suele ir con otro color.
      const f = fechaDe(v);
      const n = Number(regla.dias);
      return f !== null && Number.isFinite(n) && f >= hoy && f <= hoy + n;
    }
    case "vacia": return v === "" || v == null;
    case "noVacia": return !(v === "" || v == null);
    default: return false;
  }
}

/* Lo que devuelve una fórmula personalizada, ¿cuenta como «sí»? */
const verdadero = (v) => v === true || (typeof v === "number" && v !== 0)
  || (typeof v === "string" && v.trim().toUpperCase() === "VERDADERO");

/* ── Describir una regla (para el panel) ─────────────────────────────────── */

const num = (x) => textoDe(x);
export function describirRegla(regla) {
  switch (regla.tipo) {
    case "mayor": return `mayor que ${num(regla.valor)}`;
    case "menor": return `menor que ${num(regla.valor)}`;
    case "igual": return `igual a ${num(regla.valor)}`;
    case "entre": return `entre ${num(regla.valor)} y ${num(regla.valor2)}`;
    case "contiene": return `el texto contiene «${regla.texto}»`;
    case "textoIgual": return `el texto es «${regla.texto}»`;
    case "vencida": return "la fecha ya venció";
    case "venceEn": return `vence en los próximos ${regla.dias} días`;
    case "vacia": return "está vacía";
    case "noVacia": return "no está vacía";
    case "formula": return `la fórmula ${regla.formula} da VERDADERO`;
    default: return regla.tipo;
  }
}

/* ── Calcular, una vez por render ───────────────────────────────────────── */

/* Las fórmulas sueltas se calculan como celdas de una hoja de mentira con este
   nombre (nadie puede llamar así a una hoja: lleva un carácter de control). */
const HOJA_REGLAS = "\u0000reglas";
const RE_CELDA = /^\$?[A-Za-z]{1,3}\$?\d{1,7}$/;

/* Le pone nombre de hoja a cada referencia que no lo tiene: «=$E2<15» en la
   hoja Viajes pasa a ser «='Viajes'!$E2<15». Así, calculada en la hoja de
   mentira, sigue mirando la hoja de verdad. Respeta los textos entre comillas,
   las que ya nombran hoja (Listas!A1, 'Mi hoja'!A1), las funciones y la
   segunda esquina de un rango (el motor toma la hoja de la primera). */
export function calificarFormula(formula, hoja) {
  const s = String(formula ?? "").trim();
  if (!s.startsWith("=")) return s;
  const prefijo = `${citarHoja(hoja)}!`;
  const ultimo = (txt) => txt.trimEnd().slice(-1);
  let out = "=";
  let i = 1;
  while (i < s.length) {
    const ch = s[i];
    if (ch === '"' || ch === "'") { // un texto o un nombre de hoja: tal cual
      let j = i + 1;
      while (j < s.length) {
        if (s[j] === ch) { if (s[j + 1] === ch) { j += 2; continue; } j += 1; break; }
        j += 1;
      }
      out += s.slice(i, j); i = j; continue;
    }
    if (ch >= "0" && ch <= "9") {
      let j = i + 1;
      while (j < s.length && ((s[j] >= "0" && s[j] <= "9") || s[j] === ".")) j += 1;
      out += s.slice(i, j); i = j; continue;
    }
    if (/[\p{L}_$]/u.test(ch)) {
      let j = i + 1;
      while (j < s.length && /[\p{L}\p{N}_.$]/u.test(s[j])) j += 1;
      const palabra = s.slice(i, j);
      let k = j;
      while (k < s.length && s[k] === " ") k += 1;
      const yaTieneHoja = ultimo(out) === "!" || ultimo(out) === ":";
      const esRef = s[j] !== "!" && s[k] !== "(" && !yaTieneHoja && RE_CELDA.test(palabra);
      out += esRef ? prefijo + palabra : palabra;
      i = j; continue;
    }
    out += ch; i += 1;
  }
  return out;
}

/* Calcula fórmulas sueltas «como si estuvieran» en la hoja `h` del libro, en
   una sola pasada del motor. Devuelve sus resultados en orden. */
export function calcularFormulas(formulas, h, libro, hub = null) {
  if (!formulas.length) return [];
  const celdas = formulas.map((f) => [calificarFormula(f, h.nombre)]);
  const hojas = [...libro, { nombre: HOJA_REGLAS, celdas }];
  return evaluar(celdas, { hojas, hojaActual: HOJA_REGLAS, hub }).map((fila) => fila[0]);
}

const VACIO = Object.freeze({ estilos: new Map(), listas: new Map(), invalidas: new Map() });

/* Recorre cada celda del rango (recortado a la hoja). */
function cadaCelda(rg, filas, cols, fn) {
  const r1 = Math.min(rg.r1, filas - 1);
  const c1 = Math.min(rg.c1, cols - 1);
  for (let r = rg.r0; r <= r1; r += 1) for (let c = rg.c0; c <= c1; c += 1) fn(r, c);
}

/* Una fuente «=Listas!A1:A5» (o «=A1:A5», de la misma hoja) → la hoja y el
   rango. null si no se entiende. */
export function parseFuente(txt) {
  const s = String(txt ?? "").trim().replace(/^=/, "");
  const m = /^(?:'((?:[^']|'')+)'|([^'!]+))!(.+)$/.exec(s);
  const hoja = m ? (m[1] != null ? m[1].replace(/''/g, "'") : m[2].trim()) : null;
  const rg = parseRango(m ? m[3] : s);
  return rg ? { hoja, rango: rg } : null;
}

const citarHoja = (nombre) => `'${String(nombre).replace(/'/g, "''")}'`;

/* Las opciones de una lista desplegable: las escritas, o lo que se ve en el
   rango fuente (sin vacías ni repetidas, en el orden en que aparecen).
   `leer(formulas)` calcula fórmulas sueltas en la hoja actual. */
export function opcionesDeValidacion(regla, { valores = [], leer = null, hojaActual = null } = {}) {
  const vistas = new Set();
  const out = [];
  const sumar = (v) => {
    const t = textoDe(v).trim();
    if (!t || esError(v) || vistas.has(t.toLowerCase()) || out.length >= MAX_OPCIONES) return;
    vistas.add(t.toLowerCase());
    out.push(t);
  };
  if (Array.isArray(regla.lista)) {
    regla.lista.forEach(sumar);
    return out;
  }
  const f = parseFuente(regla.fuente);
  if (!f) return out;
  const { r0, c0, r1, c1 } = f.rango;
  if ((r1 - r0 + 1) * (c1 - c0 + 1) > MAX_CELDAS_FUENTE) return out;
  const mismaHoja = f.hoja == null || (hojaActual != null && f.hoja.toLowerCase() === String(hojaActual).toLowerCase());
  if (mismaHoja) {
    for (let r = r0; r <= r1; r += 1) for (let c = c0; c <= c1; c += 1) sumar(valores[r] ? valores[r][c] : "");
    return out;
  }
  if (!leer) return out;
  const formulas = [];
  for (let r = r0; r <= r1; r += 1) for (let c = c0; c <= c1; c += 1) formulas.push(`=${citarHoja(f.hoja)}!${refDe(r, c)}`);
  leer(formulas).forEach(sumar);
  return out;
}

/* ¿El valor está en la lista? Vacío vale (todavía no se cargó). Sin distinguir
   mayúsculas ni espacios de más, como Excel. */
export function esOpcionValida(v, opciones) {
  const t = textoDe(v).trim().toLowerCase();
  if (!t) return true;
  return opciones.some((o) => o.trim().toLowerCase() === t);
}

/* El formato condicional y las listas de la hoja a la vista.
 *
 *   condicional, validacion: las reglas de la hoja;
 *   libro: [{ nombre, celdas }] y `activa` su índice (lo mismo que calcula la
 *          pantalla), para las fórmulas y las fuentes de otras hojas;
 *   valores: la matriz ya calculada de la hoja activa;
 *   hub: el de la pantalla (una fórmula personalizada puede usar HUB.…).
 *
 * Devuelve { estilos: Map(ref → estilo), listas: Map(ref → { regla, opciones }),
 * invalidas: Map(ref → mensaje) }. El `estilo` es EL MISMO objeto de la regla:
 * mientras las reglas no cambien, la celda memoizada recibe el mismo prop y no
 * se redibuja de más. */
export function calcularFormato({ condicional, validacion, libro = [], activa = 0, valores = [], hub = null, hoy = null }) {
  const reglas = condicional || [];
  const listas = validacion || [];
  if (!reglas.length && !listas.length) return VACIO;
  const h = libro[activa] || libro[0];
  if (!h) return VACIO;
  const filas = valores.length;
  const cols = filas ? valores[0].length : 0;
  const leer = (formulas) => calcularFormulas(formulas, h, libro, hub);
  const diaHoy = hoy ?? hoySerial();

  // 1) Las fórmulas personalizadas de todas las reglas, en UNA pasada del motor.
  //    Cada una se escribe para la primera celda del rango y se corre a las
  //    demás como al copiar.
  const pedidas = [];
  const dondeVa = []; // por regla: Map(ref → índice en `pedidas`)
  reglas.forEach((regla, i) => {
    const rg = parseRango(regla.rango);
    if (regla.tipo !== "formula" || !rg || !String(regla.formula || "").trim().startsWith("=")) return;
    const m = new Map();
    cadaCelda(rg, filas, cols, (r, c) => {
      m.set(refDe(r, c), pedidas.length);
      pedidas.push(desplazarFormula(String(regla.formula).trim(), r - rg.r0, c - rg.c0));
    });
    dondeVa[i] = m;
  });
  const resultados = leer(pedidas);

  // 2) En orden: la primera regla que se cumple pinta la celda.
  const estilos = new Map();
  reglas.forEach((regla, i) => {
    const rg = parseRango(regla.rango);
    if (!rg || !regla.estilo || !Object.keys(regla.estilo).length) return;
    cadaCelda(rg, filas, cols, (r, c) => {
      const ref = refDe(r, c);
      if (estilos.has(ref)) return;
      const si = regla.tipo === "formula"
        ? !!dondeVa[i] && verdadero(resultados[dondeVa[i].get(ref)])
        : cumple(regla, valores[r][c], diaHoy);
      if (si) estilos.set(ref, regla.estilo);
    });
  });

  // 3) Las listas desplegables y lo que no está en la lista.
  const conLista = new Map();
  const invalidas = new Map();
  listas.forEach((regla) => {
    const rg = parseRango(regla.rango);
    if (!rg) return;
    const opciones = opcionesDeValidacion(regla, { valores, leer, hojaActual: h.nombre });
    const info = { regla, opciones };
    const resumen = opciones.length > 8 ? `${opciones.slice(0, 8).join(", ")}…` : opciones.join(", ");
    cadaCelda(rg, filas, cols, (r, c) => {
      const ref = refDe(r, c);
      if (conLista.has(ref)) return;
      conLista.set(ref, info);
      const v = valores[r][c];
      if (!esOpcionValida(v, opciones)) {
        invalidas.set(ref, opciones.length
          ? `«${textoDe(v)}» no está en la lista: ${resumen}`
          : "La lista de opciones está vacía");
      }
    });
  });

  return { estilos, listas: conLista, invalidas };
}

/* ── Insertar y borrar filas o columnas ─────────────────────────────────────
 *
 * Lo llama `cambiarEstructura` (shared/planillaOps) para CADA hoja del libro:
 * `cambio` es `{ eje, desde, cuantas, hoja }` (la hoja que cambió) y
 * `esLaHoja` dice si `hoja` es ésa. En la que cambió se corren los rangos
 * (como un rango de una fórmula: se agrandan, se achican o, si se borró todo,
 * la regla se va). En todas, las fórmulas y las fuentes que miran a la hoja
 * que cambió se corrigen igual que las de las celdas.
 *
 * Devuelve null si no hubo nada que tocar, o `{ condicional, validacion }`. */

function ajustarRango(rango, cambio) {
  const f = ajustarFormula(`=${rango}`, cambio);
  return f.includes("#REF") ? null : f.slice(1);
}

export function ajustarReglasHoja(hoja, cambio, esLaHoja) {
  const cond = hoja.condicional || [];
  const val = hoja.validacion || [];
  if (!cond.length && !val.length) return null;
  const c = { ...cambio, hojaFormula: hoja.nombre };
  const k = cambio.eje === "col" ? "c0" : "r0";
  const borradas = cambio.cuantas < 0 ? -cambio.cuantas : 0;
  let toco = false;

  const condicional = [];
  for (const regla of cond) {
    let nueva = regla;
    if (esLaHoja) {
      const rango = ajustarRango(regla.rango, c);
      if (!rango) { toco = true; continue; } // se borró todo su rango
      if (rango !== regla.rango) nueva = { ...nueva, rango };
    }
    if (regla.tipo === "formula" && regla.formula) {
      let f = String(regla.formula);
      // La fórmula está escrita para la primera celda del rango. Si esa celda
      // se borra, la primera pasa a ser la que venía después del bloque
      // borrado: antes de corregirla se la escribe para ésa.
      const rg = parseRango(regla.rango);
      if (esLaHoja && borradas && rg && rg[k] >= cambio.desde && rg[k] < cambio.desde + borradas) {
        const paso = cambio.desde + borradas - rg[k];
        f = desplazarFormula(f, k === "r0" ? paso : 0, k === "c0" ? paso : 0);
      }
      f = ajustarFormula(f, c);
      if (f !== regla.formula) nueva = { ...nueva, formula: f };
    }
    if (nueva !== regla) toco = true;
    condicional.push(nueva);
  }

  const validacion = [];
  for (const regla of val) {
    let nueva = regla;
    if (esLaHoja) {
      const rango = ajustarRango(regla.rango, c);
      if (!rango) { toco = true; continue; }
      if (rango !== regla.rango) nueva = { ...nueva, rango };
    }
    if (typeof regla.fuente === "string" && regla.fuente.trim()) {
      const src = regla.fuente.trim().startsWith("=") ? regla.fuente.trim() : `=${regla.fuente.trim()}`;
      const f = ajustarFormula(src, c);
      if (f !== src) nueva = { ...nueva, fuente: f };
    }
    if (nueva !== regla) toco = true;
    validacion.push(nueva);
  }
  return toco ? { condicional, validacion } : null;
}

/* ── Copiar y pegar ─────────────────────────────────────────────────────────
 *
 * Al copiar un bloque viajan, como en Excel, las reglas que lo tocan,
 * recortadas a lo copiado (la fórmula se reescribe para la nueva primera
 * celda). Al pegar se corren lo que se corrió el bloque y van ADELANTE de las
 * que ya tenía el destino: lo pegado manda, como manda el formato pegado. Una
 * regla idéntica a una que ya está no se duplica. Cortar copia las reglas
 * pero no las saca del origen (son de un rango, no de cada celda). */

export function reglasDelRango(hoja, rg) {
  const condicional = [];
  for (const regla of hoja.condicional || []) {
    const r = parseRango(regla.rango);
    if (!r || !seCruzan(r, rg)) continue;
    const parte = recortar(r, rg);
    const nueva = { ...regla, rango: textoRango(parte) };
    if (regla.tipo === "formula" && regla.formula) {
      nueva.formula = desplazarFormula(regla.formula, parte.r0 - r.r0, parte.c0 - r.c0);
    }
    condicional.push(nueva);
  }
  const validacion = [];
  for (const regla of hoja.validacion || []) {
    const r = parseRango(regla.rango);
    if (!r || !seCruzan(r, rg)) continue;
    validacion.push({ ...regla, rango: textoRango(recortar(r, rg)) });
  }
  return condicional.length || validacion.length ? { condicional, validacion } : null;
}

const igual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

export function pegarReglas(hoja, reglas, origen, destino) {
  if (!reglas || !origen) return hoja;
  const dr = destino.r - origen.r0;
  const dc = destino.c - origen.c0;
  const correr = (regla) => {
    const r = parseRango(regla.rango);
    const rango = textoRango({ r0: r.r0 + dr, r1: r.r1 + dr, c0: r.c0 + dc, c1: r.c1 + dc });
    const nueva = { ...regla, rango };
    if (regla.tipo === "formula" && regla.formula) nueva.formula = desplazarFormula(regla.formula, dr, dc);
    return nueva;
  };
  const sumar = (actuales, nuevas) => {
    const extra = nuevas.map(correr).filter((n) => !actuales.some((a) => igual(a, n)));
    return extra.length ? [...extra, ...actuales] : actuales;
  };
  const condicional = sumar(hoja.condicional || [], reglas.condicional || []);
  const validacion = sumar(hoja.validacion || [], reglas.validacion || []);
  if (condicional === (hoja.condicional || []) && validacion === (hoja.validacion || [])) return hoja;
  return { ...hoja, condicional, validacion };
}

/* ── Armar una regla desde el panel ─────────────────────────────────────────
 * Lo que la persona escribió (textos) → la regla, o `{ error }` con qué falta. */
export function armarRegla({ tipo, rango, valor, valor2, texto, dias, formula, estilo }) {
  const t = POR_TIPO.get(tipo);
  if (!t) return { error: "Elegí qué tiene que pasar." };
  const rg = parseRango(rango);
  if (!rg) return { error: "El rango no se entiende. Escribilo como E2:E100." };
  const regla = { rango: textoRango(rg), tipo };
  for (const campo of t.campos) {
    if (campo === "valor" || campo === "valor2") {
      const n = numeroLiteral(campo === "valor" ? valor : valor2);
      if (n === null) return { error: "Poné un número (con coma si tiene decimales)." };
      regla[campo] = n;
    } else if (campo === "dias") {
      const n = numeroLiteral(dias);
      if (n === null || n < 0 || !Number.isInteger(n)) return { error: "Poné cuántos días (un número entero)." };
      regla.dias = n;
    } else if (campo === "texto") {
      if (!String(texto ?? "").trim()) return { error: "Escribí el texto a buscar." };
      regla.texto = String(texto).trim();
    } else if (campo === "formula") {
      const f = String(formula ?? "").trim();
      if (!f) return { error: "Escribí la fórmula, empezando con =." };
      regla.formula = f.startsWith("=") ? f : `=${f}`;
    }
  }
  const e = limpiarEstilo(estilo);
  if (!Object.keys(e).length) return { error: "Elegí un color de fondo, de texto o negrita." };
  regla.estilo = e;
  return { regla };
}

/* Lo que la persona escribió para una lista → la regla, o `{ error }`. Las
   opciones van separadas por coma, punto y coma o renglón. */
export function armarValidacion({ rango, modo, opciones, fuente }) {
  const rg = parseRango(rango);
  if (!rg) return { error: "El rango no se entiende. Escribilo como C2:C50." };
  if (modo === "rango") {
    const f = parseFuente(fuente);
    if (!f) return { error: "El rango de opciones no se entiende. Escribilo como =Listas!A1:A5." };
    const hoja = f.hoja == null ? "" : `${/^[\p{L}_][\p{L}\p{N}_.]*$/u.test(f.hoja) ? f.hoja : citarHoja(f.hoja)}!`;
    return { regla: { rango: textoRango(rg), fuente: `=${hoja}${textoRango(f.rango)}` } };
  }
  const lista = [];
  for (const o of String(opciones ?? "").split(/[\n;,]/)) {
    const t = o.trim();
    if (t && !lista.some((x) => x.toLowerCase() === t.toLowerCase())) lista.push(t);
  }
  if (!lista.length) return { error: "Escribí al menos una opción." };
  return { regla: { rango: textoRango(rg), lista } };
}
